import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useAuthStore } from "../../store/useAuthStore";
import { PlatformEnum, PlatformUrlMapping } from "../../shared/utils";
import { Input, Label } from "../../shared/InputField";
import { decodeAuthToken } from "../../utils/auth";
import { logoDarkImage } from "../../shared/images";
import { AuthStrategyType, UserType } from "../../types/auth";
import { EmailOTPType, useForgotPassword, useGetUserInfo, useSignIn } from "../../queries/auth";
import { passkeyAuthenticateBegin, passkeyAuthenticateFinish } from "../../api/auth";
import { startAuthentication } from "@simplewebauthn/browser";
import {
  AccountSelectionComponent,
  EmailComponent,
  PasswordComponent,
  WarningModal,
} from "./LoginComponents";
import SessionStorageService, {
  SessionStorageKeys,
} from "../../utils/sessionStorage";
import Loader from "../../utils/loader";
import { useGoogleAuthStore } from "../../store/useGoogleAuthStore";
import { ArrowLeft, ArrowLeftFromLine } from "lucide-react";
import { trackEvent } from "../../utils/AmpAnalytics";
import { PrivacyPolicy } from "./privacyPolicy";
import { useFeatureFlag } from "../../FeatureFlags";
import { WaitlistTopBar } from "../../layout/TopBar/WaitlistTopBar";

export const loginPageImageUrl = ["/login.png", "/login2.png", "/login3.png"];

/** Convert backend errors to safe, user-facing messages. */
function safeErrorMessage(err: any, fallback: string): string {
  const raw: string =
    err?.response?.data?.message ?? err?.message ?? "";
  if (
    !raw ||
    raw.includes("at ") ||
    raw.includes("Error:") ||
    raw.length > 200
  ) {
    return fallback;
  }
  return raw;
}

/** Only allow redirects to known origins derived from PlatformUrlMapping env vars. */
function isAllowedRedirectUrl(url: string | undefined): url is string {
  if (!url) return false;
  if (url.startsWith("/")) return true;
  try {
    const allowedOrigins = [
      process.env.REACT_APP_INVESTOR_URL,
      process.env.REACT_APP_TASKMANAGER_URL,
      process.env.REACT_APP_CAPTABLE_URL,
      process.env.REACT_APP_ESOP_URL,
      process.env.REACT_APP_EMPLOYEE_URL,
      process.env.REACT_APP_MARKETPLACE_URL,
    ]
      .filter(Boolean)
      .map((u) => new URL(u!).origin);
    const parsed = new URL(url);
    return allowedOrigins.includes(parsed.origin);
  } catch {
    return false;
  }
}

export default function NewLoginPage() {
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: forgotPassword } = useForgotPassword();
  

  const queryParams = new URLSearchParams(location.search);
  let platform: PlatformEnum | null | undefined = queryParams.get(
    "platform"
  ) as PlatformEnum;
  const mappedRedirect = platform ? PlatformUrlMapping[platform] : undefined;
  let redirect = isAllowedRedirectUrl(mappedRedirect) ? mappedRedirect : undefined;

  const imageUrl = loginPageImageUrl;
  const length = 3;
  let count = 0;
  const { setAuth } = useAuthStore();
  const isMarketplace = useFeatureFlag("allowUserRegistration");
  const isPasskeyFeatureEnabled = useFeatureFlag("enablePasskey");

  useEffect(() => {
    const intervalId = setInterval(() => {
      count++;
      setActiveIndex(count % length);
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const sessionService = new SessionStorageService();
  const currentLoginStep = sessionService.getItem(
    SessionStorageKeys.LOGIN_STEP
  );
  const userInfo = sessionService.getItem(
    SessionStorageKeys.ALLOWED_ACCESS
  ) as any[];

  const isGoogleLogin = sessionService.getItem(
    SessionStorageKeys.GOOGLE_TOKEN
  ) as string;

  const {
    email: googleEmail,
    isLoading,
    clear: clearGoogleAuth,
    data: googleData,
    accessToken: googleToken,
    microsoftToken,
  } = useGoogleAuthStore();

  const { mutate: signIn } = useSignIn();
  const [email, setEmail] = useState<string>();
  const [loginStep, setLoginStep] = useState<number>(1);
  const [selectedRole, setSelectedRole] = useState<UserType>();
  const [currentUserType, setCurrentUserType] = useState<any>();
  const [lastUsedProduct, setLastUsedProduct] = useState<PlatformEnum>();
  const [allowedAccess, setAllowedAccess] = useState<string[]>(
    (isGoogleLogin &&
      isGoogleLogin !== null &&
      (userInfo?.reduce(
        (acc: string[], access: any) => [...acc, access.type],
        []
      ) as string[])) ||
      []
  );
  const [enabled, setEnabled] = useState(false);
  const [authStrategies, setAuthStrategies] = useState<string[]>();
  const [passkeyIntent, setPasskeyIntent] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const isPasskeySupported =
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function";

  const { data, isSuccess, isError, error } = useGetUserInfo(email!, enabled);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    header: string;
  }>({ open: false, header: "" });
  useEffect(() => {
    if (isError) {
      toast(safeErrorMessage(error, "Something went wrong. Please try again."), {
        type: "error",
        autoClose: 2000,
      });
      useGoogleAuthStore.setState((store) => ({ ...store, isLoading: false }));
      setEnabled(false);
      setLoading(false);
      setPasskeyIntent(false);
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess) {
      setLoading(false);
      setEnabled(false);

      if (data) {
        sessionService.setItem(SessionStorageKeys.ALLOWED_ACCESS, data);
        sessionService.setItem(SessionStorageKeys.EMAIL, email);
        const newAccess = data.reduce(
          (acc: string[], access: any) => {
            if (access.type && !acc.includes(access.type)) {
              if (selectedRole === undefined) setSelectedRole(access.type);
              if (lastUsedProduct === undefined)
                setLastUsedProduct(access.lastUsedProduct);
              acc.push(access.type);
            }
            return acc;
          },
          [...allowedAccess]
        );
        if (data?.length === 1) setCurrentUserType(data[0]);
        setAllowedAccess(newAccess);
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (allowedAccess.length > 1) {
      setLoginStep(2); // passkeyIntent preserved; Step 2 lets user pick role
    }
    if (allowedAccess.length === 1) {
      if (
        authStrategies &&
        !authStrategies.includes(AuthStrategyType.PASSWORD) &&
        !authStrategies.includes(AuthStrategyType.EMAIL_OTP) &&
        !authStrategies.includes(AuthStrategyType.PASSKEY)
      ) {
        toast(
          "Password and email OTP login are disabled for this account. Please sign in using Google.",
          {
            type: "error",
            autoClose: 2000,
          }
        );
        ResetEmail();
      } else if (allowedAccess[0] === UserType.EMPLOYEE) {
        if (passkeyIntent) {
          if (authStrategies === undefined) return; // wait for authStrategies to resolve
          if (authStrategies.includes(AuthStrategyType.PASSKEY)) {
            handlePasskeyLogin(UserType.EMPLOYEE);
          } else {
            window.location.href =
              process.env.REACT_APP_EMPLOYEE_URL ||
              "https://employee.dev.hissa.com";
            setTimeout(() => ResetEmail(false), 100);
          }
        } else {
          window.location.href =
            process.env.REACT_APP_EMPLOYEE_URL ||
            "https://employee.dev.hissa.com";
          setTimeout(() => ResetEmail(false), 100);
        }
      } else {
        // EQUION
        if (passkeyIntent) {
          if (authStrategies === undefined) return; // wait for authStrategies to resolve
          if (authStrategies.includes(AuthStrategyType.PASSKEY)) {
            handlePasskeyLogin(UserType.EQUION);
          } else {
            toast("No passkey registered. Use Continue to sign in.", {
              type: "info",
              autoClose: 3000,
            });
            setPasskeyIntent(false);
            setLoading(false);
          }
        } else {
          setLoginStep(3);
        }
      }
    }
  }, [allowedAccess, authStrategies]);

  useEffect(() => {
    if (selectedRole && userInfo) {
      const data = userInfo as any[];
      const foundIndex = data?.findIndex(
        (currentData: any) => currentData.type === selectedRole
      );
      if (foundIndex !== -1) {
        setAuthStrategies(data[foundIndex].authStrategies);
      }
    }
  }, [selectedRole]);

  useEffect(() => {
    if (currentLoginStep && currentLoginStep !== null) {
      const allowedType = userInfo?.reduce(
        (acc: string[], access: any) => [...acc, access.type],
        []
      );
      setSelectedRole(
        allowedType[0] === "EQUION"
          ? UserType.EQUION
          : allowedType[0] === "EMPLOYEE"
          ? UserType.EMPLOYEE
          : undefined
      );
      setLastUsedProduct(allowedType[0]?.lastUsedProduct);
      // setCurrentUserType(allowedAccess[0])
      setLoginStep(Number(currentLoginStep));
      useGoogleAuthStore.setState((store) => ({ ...store, isLoading: false }));
    }
  }, [currentLoginStep]);

  function ResetEmail(isNavigatingToLogin = true) {
    setEmail(undefined);
    clearGoogleAuth();
    setSelectedRole(undefined);
    setAuthStrategies(undefined);
    setLastUsedProduct(undefined);
    setCurrentUserType(undefined);
    setAllowedAccess([]);
    setPasskeyIntent(false);
    sessionService.clear();
    useGoogleAuthStore.setState((store) => ({ ...store, isLoading: false }));
    if (isNavigatingToLogin) setLoginStep(1);
  }

  async function handleUsernamelessPasskeyLogin() {
    setPasskeyLoading(true);
    try {
      const beginRes = await passkeyAuthenticateBegin({});
      const { transactionId, options } = beginRes.data;
      const authResponse = await startAuthentication({ optionsJSON: options });
      const finishRes = await passkeyAuthenticateFinish({
        authStrategyType: "PASSKEY",
        passkeyTransactionId: transactionId,
        passkeyAuthenticationResponse: authResponse,
      });
      const accessToken = finishRes.data;
      const decodedJwt = decodeAuthToken(accessToken);
      sessionService.clear();
      toast("Signin Successful", { type: "success", autoClose: 2000 });
      trackEvent("Logging In with Passkey", {
        email: decodedJwt.emailId,
        name: decodedJwt.fullName,
      });
      if (redirect) {
        window.location.href = redirect;
      } else {
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.emailId,
          name: decodedJwt.fullName,
          id: decodedJwt.id,
        });
        navigate("/landing/dashboard");
      }
    } catch (err: any) {
      const msg = safeErrorMessage(err, "Passkey login failed");
      if (
        msg.toLowerCase().includes("cancel") ||
        err?.name === "NotAllowedError"
      ) {
        toast("Cancelled. Please try again.", { type: "info", autoClose: 2000 });
      } else if (
        msg.toLowerCase().includes("not found") ||
        msg.toLowerCase().includes("invalid") ||
        msg.toLowerCase().includes("credential")
      ) {
        toast(
          "This passkey is no longer valid. Please remove it from your password manager and try a different sign-in method.",
          { type: "error", autoClose: 5000 }
        );
      } else {
        toast(msg, { type: "error", autoClose: 2000 });
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function handlePasskeyLogin(userType: UserType) {
    setPasskeyLoading(true);
    try {
      const beginRes = await passkeyAuthenticateBegin({ email: email!, userType });
      const { transactionId, options } = beginRes.data;
      const authResponse = await startAuthentication({ optionsJSON: options });
      const finishRes = await passkeyAuthenticateFinish({
        email: email!,
        userType,
        authStrategyType: "PASSKEY",
        passkeyTransactionId: transactionId,
        passkeyAuthenticationResponse: authResponse,
      });
      const accessToken = finishRes.data;
      const decodedJwt = decodeAuthToken(accessToken);
      sessionService.clear();
      setPasskeyIntent(false);
      toast("Signin Successful", { type: "success", autoClose: 2000 });
      trackEvent("Logging In with Passkey", {
        email: decodedJwt.emailId || email,
        name: decodedJwt.fullName,
      });
      if (redirect) {
        window.location.href = redirect;
      } else if (lastUsedProduct) {
        window.location.href =
          PlatformUrlMapping[lastUsedProduct as PlatformEnum] || "/landing/dashboard";
      } else {
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.emailId,
          name: decodedJwt.fullName,
          id: decodedJwt.id,
        });
        navigate("/landing/dashboard");
      }
    } catch (err: any) {
      setPasskeyIntent(false);
      const msg = safeErrorMessage(err, "Passkey login failed");
      if (
        msg.toLowerCase().includes("cancel") ||
        err?.name === "NotAllowedError"
      ) {
        toast("Cancelled. Please try again.", { type: "info", autoClose: 2000 });
      } else if (
        msg.toLowerCase().includes("not found") ||
        msg.toLowerCase().includes("invalid") ||
        msg.toLowerCase().includes("credential")
      ) {
        toast(
          "This passkey is no longer valid. Please remove it from your password manager and try a different sign-in method.",
          { type: "error", autoClose: 5000 }
        );
      } else {
        toast(msg, { type: "error", autoClose: 2000 });
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  function HandleSelectedRole() {
    if (
      (googleToken && googleToken !== "") ||
      (microsoftToken && microsoftToken !== "")
    ) {
      setLoading(true);
      signIn(
        {
          email: googleEmail || "",
          authStrategyType: microsoftToken
            ? AuthStrategyType.MICROSOFT
            : AuthStrategyType.GOOGLE,
          userType:
            selectedRole === "EQUION" ? UserType.EQUION : UserType.EMPLOYEE,
          microsoftAuthCode: microsoftToken || "",
          googleIdToken: googleToken || "",
        },
        {
          onSuccess: (data) => {
            setLoading(false);
            if (data.data) {
              const accessToken = data.data;
              const decodedJwt = decodeAuthToken(accessToken);
              trackEvent("Logging In with Google", {
                email: googleEmail,
                name: decodedJwt.fullName,
              });
              const userType = selectedRole === "EQUION" ? "Admin" : "Employee";
              toast("Signin Successful Signed in as " + userType, {
                type: "success",
                autoClose: 2000,
              });
              if (redirect) {
                window.location.href = redirect;
              } else if (
                googleData.data !== undefined &&
                googleData?.data?.length > 0 &&
                redirect === undefined
              ) {
                const allowedType = googleData.data?.find(
                  (data: any) => data.type === selectedRole
                );
                if (isMarketplace) {
                  const marketplaceLink = process.env.REACT_APP_MARKETPLACE_URL;
                  window.location.href = marketplaceLink || "/login";
                } else if (allowedType && allowedType?.lastUsedProduct) {
                  const lastUsedProductLink =
                    PlatformUrlMapping[
                      allowedType?.lastUsedProduct as PlatformEnum
                    ];
                  window.location.href =
                    lastUsedProductLink || "/landing/dashboard";
                } else {
                  setAuth({
                    accessToken,
                    isAuthenticated: true,
                    email: decodedJwt.emailId,
                    name: decodedJwt.fullName,
                    id: decodedJwt.id,
                  });
                  navigate("/landing/dashboard");
                }
              } else {
                setAuth({
                  accessToken,
                  isAuthenticated: true,
                  email: decodedJwt.emailId,
                  name: decodedJwt.fullName,
                  id: decodedJwt.id,
                });
                navigate("/landing/dashboard");
              }

              sessionService.removeItem(SessionStorageKeys.GOOGLE_TOKEN);
              sessionService.removeItem(SessionStorageKeys.ALLOWED_ACCESS);
              sessionService.removeItem(SessionStorageKeys.LOGIN_STEP);
              clearGoogleAuth();
            }
          },
          onError: (err: any) => {
            setLoading(false);
            setShowWarning(false);
            toast(safeErrorMessage(err, "Something went wrong, try again later"), {
              type: "error",
              autoClose: 2000,
            });
            setLoginStep(1);
            ResetEmail();
          },
        }
      );
    } else {
      const allowedType = userInfo?.find(
        (data: any) => data.type === selectedRole,
        []
      );
      if (
        !allowedType?.authStrategies.includes(AuthStrategyType.PASSWORD) &&
        !allowedType?.authStrategies.includes(AuthStrategyType.EMAIL_OTP) &&
        !allowedType?.authStrategies.includes(AuthStrategyType.PASSKEY)
      ) {
        toast(
          "Password and email OTP login are disabled for this account. Please sign in using Google.",
          {
            type: "error",
            autoClose: 2000,
          }
        );
        ResetEmail();
      } else {
        setCurrentUserType(allowedType);
        setLoginStep(3);
      }
    }
  }

  const hidePasswordOption =
    currentUserType?.isApplicationSetPassword === true &&
    currentUserType?.isPasswordSet === false;

  return (
    <>
      <div className={`w-screen px-4 px-md-0 h-screen bg-gradient-to-t from-gradientFrom to-gradientTo flex flex-col items-center justify-center ${isMarketplace ? 'pt-6' : ''} overflow-y-auto`}>
        <div
          className={`w-full max-w-[500px]  bg-white rounded-lg shadow-lg ${isMarketplace ? "min-h-[600px]" : "min-h-[550px]"}`}
        >
          

          {isMarketplace && loginStep === 1 &&
            <WaitlistTopBar />
          }
          
          <div
            className="md:flex md:flex-col justify-center items-center flex-1 pb-12 mx-auto w-full p-4 sm:p-6"
            style={{ position: "relative" }}
          >
            {loginStep === 2 && (
              <ArrowLeft
                className="
                  absolute
                  top-4
                  left-4
                  cursor-pointer
                  text-gray-600
                  hover:text-primary
                  transition-colors
                "
                onClick={() => {
                  setLoginStep(1)
                  ResetEmail()
                }}
              />
            )}
            
            <div className="">
              {logoDarkImage ? (
                <img
                  className={` mx-auto ${(isMarketplace && loginStep === 1) ? 'mb-0 h-8 w-20' : 'mb-8 h-16 w-28 pt-[15px]'}`}
                  src={logoDarkImage}
                  alt="hissa_logo"
                />
              ) : (
                <h2 className="text-4xl font-semibold text-secondary">
                  {"hissa"}
                </h2>
              )}
            </div>

            {isLoading && <Loader />}

            <div className="text-center justify-center">
              {loginStep === 1 && (
                <>
                  <EmailComponent
                    email={email}
                    setEmail={(value: string) => {
                      setEmail(value);
                      setLoading(true);
                      setEnabled(true);
                    }}
                    loading={loading}
                    isMarketplace={isMarketplace}
                    isPasskeySupported={isPasskeyFeatureEnabled && isPasskeySupported}
                    hasPasskeyRegistered={false}
                    passkeyLoading={passkeyLoading}
                    onPasskeyLogin={(emailValue: string) => {
                      setEmail(emailValue);
                      setPasskeyIntent(true);
                      setLoading(true);
                      setEnabled(true);
                    }}
                    onUsernamelessPasskeyLogin={handleUsernamelessPasskeyLogin}
                  />
                  {/* {isMarketplace && (
                    <div className="mt-4 text-xs text-gray-600">
                      Don't have an account?{" "}
                      <span
                        className="text-primary text-sm cursor-pointer font-semibold hover:underline"
                        onClick={() => navigate("/register")}
                      >
                        Register now
                      </span>
                    </div>
                  )} */}
                </>
              )}

              {loginStep === 2 && (
                <AccountSelectionComponent
                  selectedRole={selectedRole}
                  setSelectedRole={(value: UserType) => {
                    setSelectedRole(value);
                  }}
                  onSubmit={() => {
                    if (passkeyIntent) {
                      if (selectedRole === UserType.EMPLOYEE) {
                        window.location.href =
                          process.env.REACT_APP_EMPLOYEE_URL ||
                          "https://employee.dev.hissa.com";
                        setTimeout(() => ResetEmail(false), 100);
                      } else {
                        handlePasskeyLogin(selectedRole!);
                      }
                    } else if (selectedRole === UserType.EMPLOYEE) {
                      window.location.href =
                        process.env.REACT_APP_EMPLOYEE_URL ||
                        "https://employee.dev.hissa.com";
                      setTimeout(() => ResetEmail(false), 100);
                    } else if (googleData && googleData?.data) {
                      const allowedType = googleData.data?.find(
                        (data: any) => data.type === selectedRole
                      );
                      if (
                        allowedType?.hasActiveSession === true &&
                        allowedType?.allowMultiAccess === false
                      ) {
                        setShowWarning(true);
                      } else HandleSelectedRole();
                    } else HandleSelectedRole();
                  }}
                  loading={loading}
                  isGoogleLogin={(googleToken && googleToken !== null) || false}
                  authStrategies={authStrategies}
                />
              )}

              {loginStep === 3 && (
                <PasswordComponent
                  email={email}
                  selectedRole={selectedRole}
                  authStrategies={authStrategies}
                  isOTP={authStrategies?.includes(AuthStrategyType.EMAIL_OTP)}
                  isPassword={
                    authStrategies?.includes(AuthStrategyType.PASSWORD) &&
                    !hidePasswordOption
                  }
                  onClearEmail={() => {
                    ResetEmail();
                  }}
                  redirect
                  lastUsedProduct={lastUsedProduct}
                  currentUserType={currentUserType}
                  isMarketplace={isMarketplace}
                />
              )}
            </div>
          </div>
        </div>
        <div className=" mt-2 mb-8">
          <PrivacyPolicy />
        </div>
        {isMarketplace && (
            <p className='attribution flex justify-center text-xxs text-white'>
              <a href='https://logo.dev' target='_blank' onClick={() => trackEvent('Navigating to Logo.dev')}>
                Logos provided by Logo.dev
              </a>
            </p>
          )}
        {showWarning && (
          <WarningModal
            open={showWarning}
            onClose={() => setShowWarning(false)}
            onSubmit={() => HandleSelectedRole()}
            isLoading={loading}
          />
        )}
      </div>
    </>
  );
}
